import { z } from 'zod'

const recipientIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(256)
  .refine(
    (value) =>
      ![...value].some((character) => {
        const codePoint = character.codePointAt(0) ?? 0
        return (
          '.#$/[]'.includes(character) || codePoint <= 31 || codePoint === 127
        )
      })
  )

const recipientFields = {
  id: recipientIdSchema.nullable(),
  name: z
    .string()
    .trim()
    .min(1, 'Enter a recipient name.')
    .max(100, 'Recipient names must be 100 characters or fewer.'),
  email: z
    .string()
    .trim()
    .email('Enter a valid email address.')
    .max(254, 'Email addresses must be 254 characters or fewer.')
    .nullable(),
  phone: z
    .string()
    .trim()
    .regex(
      /^\+[1-9][0-9]{7,14}$/,
      'Enter a phone number in E.164 format, such as +15165550123.'
    )
    .max(16)
    .nullable(),
  enabled: z.boolean(),
  emailActive: z.boolean(),
  smsActive: z.boolean(),
  appointmentBooked: z.boolean(),
  callbackRequested: z.boolean(),
  cancellationRequested: z.boolean(),
  rescheduleRequested: z.boolean(),
  needsAttention: z.boolean(),
  appointmentBookedSms: z.boolean(),
  callbackRequestedSms: z.boolean(),
  cancellationRequestedSms: z.boolean(),
  rescheduleRequestedSms: z.boolean(),
  needsAttentionSms: z.boolean(),
}

export const notificationRecipientPayloadSchema = z
  .object(recipientFields)
  .strict()
  .superRefine((recipient, context) => {
    if (recipient.emailActive && recipient.email === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: 'Enter an email address or disable email delivery.',
      })
    }
    const emailEventEnabled =
      recipient.appointmentBooked ||
      recipient.callbackRequested ||
      recipient.cancellationRequested ||
      recipient.rescheduleRequested ||
      recipient.needsAttention
    const smsEventEnabled =
      recipient.appointmentBookedSms ||
      recipient.callbackRequestedSms ||
      recipient.cancellationRequestedSms ||
      recipient.rescheduleRequestedSms ||
      recipient.needsAttentionSms
    if (smsEventEnabled && recipient.phone === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phone'],
        message: 'Enter a staff phone number before selecting SMS events.',
      })
    }
    if (
      recipient.enabled &&
      !(
        (recipient.emailActive && emailEventEnabled) ||
        (recipient.smsActive && smsEventEnabled)
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['enabled'],
        message: 'Choose at least one event on an enabled delivery channel.',
      })
    }
    if (
      recipient.id === null &&
      recipient.email === null &&
      recipient.phone === null
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phone'],
        message: 'Enter an email address or phone number for a new recipient.',
      })
    }
  })

export const notificationSettingsPayloadSchema = z
  .object({
    recipients: z.array(notificationRecipientPayloadSchema).max(25),
  })
  .strict()
  .superRefine((settings, context) => {
    const ids = new Set<string>()
    const emails = new Set<string>()
    const phones = new Set<string>()
    settings.recipients.forEach((recipient, index) => {
      if (recipient.id) {
        if (ids.has(recipient.id)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recipients', index, 'id'],
            message: 'Duplicate recipient',
          })
        }
        ids.add(recipient.id)
      }
      if (recipient.email) {
        const email = recipient.email.toLowerCase()
        if (emails.has(email)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recipients', index, 'email'],
            message: 'Duplicate recipient email',
          })
        }
        emails.add(email)
      }
      if (recipient.phone) {
        if (phones.has(recipient.phone)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recipients', index, 'phone'],
            message: 'Duplicate recipient phone',
          })
        }
        phones.add(recipient.phone)
      }
    })
  })

export const notificationSettingsResponseSchema = z
  .object({
    recipients: z
      .array(
        z
          .object({
            ...recipientFields,
            id: recipientIdSchema,
            smsConfigured: z.boolean(),
          })
          .strict()
      )
      .max(25),
    capabilities: z.object({ email: z.boolean(), sms: z.boolean() }).strict(),
    legacyConfigurationPresent: z.boolean(),
  })
  .strict()

export const notificationRecipientDeletionResponseSchema = z
  .object({ deletedRecipientId: recipientIdSchema })
  .strict()

export type NotificationRecipientPayload = z.infer<
  typeof notificationRecipientPayloadSchema
>
export type NotificationSettingsPayload = z.infer<
  typeof notificationSettingsPayloadSchema
>
export type NotificationSettingsResponse = z.infer<
  typeof notificationSettingsResponseSchema
>
export type NotificationRecipientDeletionResponse = z.infer<
  typeof notificationRecipientDeletionResponseSchema
>
